// src/features/mech/plastics/questions/MaterialSelectionQuestion.jsx
import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import { Box } from "@mui/material";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlTextField from "../../../../shared/components/PerlTextField";
import { apiFetch } from "../../../../api/apiClient";

/**
 * MaterialSelectionQuestion Component
 *
 * Custom question for Mechanical Plastics: captures the material selection
 * with validation for plastic molding materials
 *
 * @param {string|object} value - Current material value (string for material, or object with material)
 * @param {function} onChange - Callback when the material changes (receives string material)
 * @param {function} [onValidationChange] - Callback when validation state changes (receives {isValid, isMaterialValid, isValidating})
 * @param {string} [error] - Optional error message
 * @param {string} [success] - Optional success/help message
 * @param {function} [onNextStep] - Optional callback to proceed to next step on Enter key
 * @param {number} [currentStep] - Current step index
 */
const MaterialSelectionQuestion = forwardRef(({ value, onChange, onValidationChange, error, success, onNextStep, currentStep }, ref) => {
  // Handle both string (legacy) and object (new) value formats
  const materialValue = typeof value === "object" ? value?.material || "" : value || "";
  
  const [materialValidationMessage, setMaterialValidationMessage] = useState("");
  const [isValidatingMaterial, setIsValidatingMaterial] = useState(false);
  const [isMaterialValid, setIsMaterialValid] = useState(false);

  // Validate material type via API for plastics
  const validateMaterialType = useCallback(async (material) => {
    if (!material || material.trim() === "") {
      setMaterialValidationMessage("");
      setIsMaterialValid(false);
      if (onValidationChange) {
        onValidationChange({ isValid: false, isMaterialValid: false, isValidating: false });
      }
      return false;
    }

    setIsValidatingMaterial(true);
    if (onValidationChange) {
      onValidationChange({ isValid: false, isMaterialValid: false, isValidating: true });
    }
    
    try {
      const response = await apiFetch("/api/mech/validate-material-type", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          materialType: material,
          materialCategory: "plastics", // Specify plastics category
          // Don't include materialGrade for plastics
        }),
      });

      if (response.ok) {
        const result = await response.json();
        let message = result.message || "";
        
        // Use only the message from backend, no additional suggestions
        setMaterialValidationMessage(message);
        const isValid = result.is_valid || false;
        setIsMaterialValid(isValid);
        
        if (onValidationChange) {
          onValidationChange({ isValid, isMaterialValid: isValid, isValidating: false });
        }
        return isValid;
      } else {
        // Handle error response
        let errorMessage = "Validation failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch (jsonErr) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        console.error("Material validation API error:", errorMessage);
        setMaterialValidationMessage(`Validation error: ${errorMessage}`);
        setIsMaterialValid(false);
        if (onValidationChange) {
          onValidationChange({ isValid: false, isMaterialValid: false, isValidating: false });
        }
        return false;
      }
    } catch (err) {
      console.error("Material validation error:", err);
      setMaterialValidationMessage(`Validation error: ${err.message || "Network error"}`);
      setIsMaterialValid(false);
      if (onValidationChange) {
        onValidationChange({ isValid: false, isMaterialValid: false, isValidating: false });
      }
      return false;
    } finally {
      setIsValidatingMaterial(false);
    }
  }, [onValidationChange]);

  // Reset validation state when material value changes
  useEffect(() => {
    if (!materialValue || materialValue.trim() === "") {
      setMaterialValidationMessage("");
      setIsMaterialValid(false);
      if (onValidationChange) {
        onValidationChange({ isValid: false, isMaterialValid: false, isValidating: false });
      }
    }
  }, [materialValue, onValidationChange]);

  // Expose validation function for parent to call
  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!materialValue || materialValue.trim() === "") {
        if (onValidationChange) {
          onValidationChange({ isValid: false, isMaterialValid: false, isValidating: false });
        }
        return false;
      }
      // Validate when called explicitly (e.g., from next button)
      return await validateMaterialType(materialValue);
    },
    isValid: () => isMaterialValid && !isValidatingMaterial,
    isMaterialValid: () => isMaterialValid,
    isValidating: () => isValidatingMaterial,
  }));

  const handleMaterialChange = (newMaterial) => {
    // Pass material as string
    onChange(newMaterial);
  };

  // Handle Enter key on material field
  const handleMaterialKeyDown = (e) => {
    if (e.key === "Enter" && onNextStep && materialValue && materialValue.trim() !== "") {
      e.preventDefault();
      onNextStep();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <PerlLabelMedium sx={{ mb: 1, display: "block" }}>Select Material:</PerlLabelMedium>

      <PerlTextField
        label="Enter material type"
        placeholder="e.g., PP, PBT, PA66+30%GF or enter custom material"
        value={materialValue}
        onChange={(e) => {
          handleMaterialChange(e.target.value);
        }}
        onKeyDown={handleMaterialKeyDown}
        error={Boolean(error) || (materialValidationMessage && !isValidatingMaterial && !isMaterialValid)}
        helperText={
          error || 
          (isValidatingMaterial ? "Validating..." : "") ||
          (materialValidationMessage || "")
        }
        fullWidth
      />

      {success && (
        <PerlLabelMedium sx={{ mt: 1, color: "success.main" }}>{success}</PerlLabelMedium>
      )}
    </Box>
  );
});

MaterialSelectionQuestion.displayName = "MaterialSelectionQuestion";

export default MaterialSelectionQuestion;
